/**
 * Shell (docs/11): Welcome → Sign in → Tonight. No tab bar: Tonight is the
 * app; Settings and Detail push over it. The shell owns what every screen
 * shares — the driver's position (one GPS fix, refreshed in the foreground),
 * the search context (where and how far), preferences, and the profile.
 */
import { useEffect, useState } from 'react';
import { api, type Feed, type Opp, type Metro } from './api.js';
import { store, type SearchContext, type Prefs, type StoredAttempt } from './storage.js';
import { useGeolocation, useMinuteTick } from './hooks.js';
import { headThere } from './attempt.js';
import { scheduleReminder } from './reminders.js';
import { WelcomeScreen } from './WelcomeScreen.js';
import { AuthScreen } from './AuthScreen.js';
import { ResetScreen } from './ResetScreen.js';
import { authErrorText } from './SocialButtons.js';
import { HomeScreen } from './HomeScreen.js';
import { DetailScreen } from './DetailScreen.js';
import { SettingsScreen } from './SettingsScreen.js';
import { ProfileSheet, type ProfileValues } from './ProfileSheet.js';

type Phase = 'boot' | 'welcome' | 'auth' | 'ready';
type Screen = { kind: 'tonight' } | { kind: 'detail'; opp: Opp; feed: Feed } | { kind: 'settings' };

const EMPTY_PROFILE: ProfileValues = { display_name: null, age_band: null, use_case: null, gender: null };

export function App() {
  const [phase, setPhase] = useState<Phase>('boot');
  const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
  // An emailed link lands here as /#reset=… or /#verify=… (a fragment never
  // reaches a server). Read once, then scrub it from the address bar.
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>({ kind: 'tonight' });
  const [driverMode, setDriverMode] = useState<string>(store.mode.get() ?? 'rideshare');
  const [profile, setProfile] = useState<ProfileValues>(EMPTY_PROFILE);
  const [email, setEmail] = useState<string | null>(null);
  const [search, setSearchState] = useState<SearchContext>(() => store.search.get());
  const [prefs, setPrefs] = useState<Prefs>(() => store.prefs.get());
  const [metros, setMetros] = useState<Metro[]>([]);
  const [profileSheet, setProfileSheet] = useState<'first' | 'edit' | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailResult, setDetailResult] = useState<{ attempt: StoredAttempt } | null>(null);
  const geo = useGeolocation();
  const now = useMinuteTick();

  function setSearch(s: SearchContext) { setSearchState(s); store.search.set(s); }

  async function hydrate() {
    try {
      const me = await api.me();
      setDriverMode(store.mode.get() ?? me.driver_mode);
      setProfile({ display_name: me.display_name, age_band: me.age_band, use_case: me.use_case, gender: me.gender });
      setEmail(me.email); setEmailVerified(me.email_verified);
      setPhase('ready');
      api.metros().then((m) => setMetros(m.metros)).catch(() => {});
      // The one-time questions, after the driver has seen the app (docs/10 S1).
      if ((!me.age_band || !me.use_case) && !store.flags.get().profilePrompted) {
        setTimeout(() => setProfileSheet('first'), 1500);
      }
    } catch {
      setPhase('welcome');
    }
  }

  useEffect(() => {
    /** Returns true when the address carried an emailed link. */
    function takeLink(): boolean {
      const m = /^#(reset|verify|auth_error|welcome)=([\w-]{1,200})$/.exec(window.location.hash);
      if (!m) return false;
      history.replaceState(null, '', window.location.pathname);
      // Back from a social sign-in: either it failed (say why, on the
      // sign-in screen) or it made a new account (carry on into the app).
      if (m[1] === 'auth_error') {
        setNotice(authErrorText(m[2]!)); setAuthMode('login'); setPhase('auth');
        return true;
      }
      if (m[1] === 'welcome') return false;
      if (m[2]!.length < 20) return false;
      if (m[1] === 'reset') { setResetToken(m[2]!); return true; }
      api.verifyEmail(m[2]!)
        .then(() => setNotice('Email confirmed. Thanks.'))
        .catch(() => setNotice('That confirmation link has expired or was already used.'))
        .finally(() => { void hydrate(); });
      return true;
    }
    if (!takeLink()) void hydrate();
    // The link can also arrive while the app is already open in this tab.
    const onHash = () => { takeLink(); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  function signedOut() {
    setPhase('welcome'); setScreen({ kind: 'tonight' }); setProfile(EMPTY_PROFILE); setEmail(null);
    setDetailResult(null);
    setSearchState(store.search.get()); setPrefs(store.prefs.get());
  }

  async function headThereFromDetail(opp: Opp, feed: Feed) {
    setDetailBusy(true); setDetailError(null);
    const active = store.attempt.get();
    if (active) {
      try { await api.endAttempt(active.id, 'cancelled'); } catch { /* already closed */ }
      store.attempt.clear();
    }
    const r = await headThere(feed.decision_id, opp);
    setDetailBusy(false);
    if (r.ok) {
      if (prefs.reminders && r.attempt.leave_by) void scheduleReminder(r.attempt);
      setDetailResult({ attempt: r.attempt });
      setScreen({ kind: 'tonight' });
    } else {
      setDetailError(r.message);
    }
  }

  if (resetToken) {
    return (
      <div className="app">
        <ResetScreen token={resetToken}
          onDone={() => { setResetToken(null); setNotice('Password changed. You’re signed in.'); void hydrate(); }}
          onCancel={() => { setResetToken(null); setAuthMode('login'); setPhase('auth'); }} />
      </div>
    );
  }

  if (phase === 'boot') {
    return <div className="app"><div className="skeleton card" style={{ marginTop: 64 }} aria-label="Loading" /></div>;
  }

  if (phase === 'welcome') {
    return (
      <div className="app">
        {notice && <p className="notice" role="status">{notice}</p>}
        <WelcomeScreen
          onGetStarted={() => { setAuthMode('register'); setPhase('auth'); }}
          onSignIn={() => { setAuthMode('login'); setPhase('auth'); }} />
      </div>
    );
  }

  if (phase === 'auth') {
    return (
      <div className="app">
        {notice && <p className="notice warn" role="alert">{notice}</p>}
        <AuthScreen mode={authMode} onSignedIn={() => { setNotice(null); void hydrate(); }}
          onBack={() => { setNotice(null); setPhase('welcome'); }} />
      </div>
    );
  }

  return (
    <div className="app">
      {notice && (
        <button className="notice" role="status" onClick={() => setNotice(null)}>{notice}</button>
      )}
      {screen.kind === 'tonight' && (
        <HomeScreen
          name={profile.display_name}
          driverMode={driverMode}
          origin={geo.origin}
          locState={geo.state}
          fixedAt={geo.fixedAt}
          onRetryLocation={geo.refresh}
          search={search}
          onSearchChange={setSearch}
          prefs={prefs}
          metros={metros}
          onOpenDetail={(opp, feed) => { setDetailError(null); setScreen({ kind: 'detail', opp, feed }); }}
          onOpenSettings={() => setScreen({ kind: 'settings' })}
          detailResult={detailResult}
          onDetailResultConsumed={() => setDetailResult(null)}
        />
      )}
      {screen.kind === 'detail' && (
        <DetailScreen
          opp={screen.opp}
          now={now}
          units={prefs.units}
          hasOrigin={geo.origin !== null}
          driverMode={driverMode}
          tripActiveTitle={store.attempt.get()?.title ?? null}
          busy={detailBusy}
          error={detailError}
          onBack={() => setScreen({ kind: 'tonight' })}
          onHeadThere={() => void headThereFromDetail(screen.opp, screen.feed)}
        />
      )}
      {screen.kind === 'settings' && (
        <SettingsScreen
          name={profile.display_name}
          email={email}
          emailVerified={emailVerified}
          driverMode={driverMode}
          prefs={prefs}
          onModeChange={setDriverMode}
          onPrefsChange={setPrefs}
          onEditProfile={() => setProfileSheet('edit')}
          onBack={() => setScreen({ kind: 'tonight' })}
          onSignedOut={signedOut}
        />
      )}

      {profileSheet && (
        <ProfileSheet
          mode={profileSheet}
          initial={profile}
          onSaved={(v) => { setProfile(v); setProfileSheet(null); store.flags.set({ ...store.flags.get(), profilePrompted: true }); }}
          onClose={() => { setProfileSheet(null); store.flags.set({ ...store.flags.get(), profilePrompted: true }); }}
        />
      )}
    </div>
  );
}
